/**
 * "Employee lens" additions to Quarter at a Glance: the forward
 * order book, repeat-business loyalty wall, lost-reason truths,
 * work-mix bars, an all-time seasonality heatmap and milestone
 * counters. All data arrives pre-computed from the backend.
 */
import { useEffect, useRef, useState } from "react";
import { ArrowDown, ArrowUp, BookOpen, HeartHandshake, Info, Minus, PoundSterling, Trophy } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { InfoTip } from "./methodology";

const money = (v) =>
  v == null ? "–" : `£${Math.round(Number(v)).toLocaleString("en-GB")}`;

const moneyShort = (v) => {
  if (v == null) return "–";
  const n = Number(v);
  if (n >= 1_000_000) return `£${(n / 1_000_000).toFixed(1)}m`;
  if (n >= 1_000) return `£${Math.round(n / 1_000)}k`;
  return `£${Math.round(n)}`;
};

/* ------------------------------------------------- forward order book */

export function OrderBook({ metric, editable, onOverride }) {
  if (!metric) return null;
  return (
    <div
      className="rounded-3xl bg-[#0f3a5f] text-white px-6 py-7 sm:px-10 relative overflow-hidden q-reveal"
      data-testid="qkpi-order-book"
    >
      <div
        className="absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage: "radial-gradient(circle at 80% 30%, #fff 1px, transparent 1px)",
          backgroundSize: "22px 22px",
        }}
      />
      <div className="relative">
        <div className="flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-teal-300" />
          <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-stone-300">
            The order book · won &amp; still to deliver
          </p>
          <InfoTip id="order_book" light />
          {editable && (
            <button
              type="button"
              className="text-stone-400 hover:text-white transition-colors ml-1"
              data-testid="qkpi-edit-order_book"
              onClick={() => {
                const v = window.prompt("Override order book (£, blank = auto):", metric?.value ?? "");
                if (v !== null) onOverride("order_book", v === "" ? null : v);
              }}
            >
              ✎
            </button>
          )}
        </div>
        <div className="flex items-end gap-x-8 gap-y-3 flex-wrap mt-1">
          <p className="font-serif text-4xl sm:text-5xl leading-tight">{money(metric.value)}</p>
          {metric.months_of_work != null && (
            <p className="pb-1 text-sm text-teal-200 font-mono">
              ≈ <strong className="text-lg">{metric.months_of_work}</strong> months of booked work ahead
            </p>
          )}
          {metric.projects != null && (
            <p className="pb-1 text-sm text-stone-300 font-mono">
              across <strong>{metric.projects}</strong> live projects
            </p>
          )}
          {metric.overridden && (
            <span className="pb-1 text-[9px] font-mono uppercase text-amber-400">edited</span>
          )}
        </div>
        <p className="text-[10px] text-stone-400 mt-2">
          Commissioned or contracted work not yet invoiced, at today&apos;s pipeline state.
        </p>
      </div>
    </div>
  );
}

/* ------------------------------------------------- repeat business */

export function RepeatBusiness({ data }) {
  if (!data || !data.total_clients) return null;
  return (
    <div className="rounded-2xl border border-[#1B4F8A]/10 bg-[#eef4fa] shadow-sm shadow-[#1B4F8A]/5 p-5 q-card q-reveal" data-testid="qkpi-repeat-business">
      <div className="flex items-center gap-2">
        <HeartHandshake className="w-4 h-4 text-[#2a9d8f]" />
        <p className="text-[10px] font-mono uppercase tracking-widest text-stone-400">
          Clients who come back · since Jan 2023
        </p>
        <InfoTip id="repeat_business" />
      </div>
      <div className="flex items-end gap-3 mt-2">
        <p className="font-semibold tracking-tight text-4xl text-slate-900 leading-none">{data.repeat_pct}%</p>
        <p className="text-xs text-stone-500 pb-0.5">
          {data.repeat_clients} of the {data.total_clients} companies that have commissioned us since Jan 2023 came back for more.
        </p>
      </div>
      {data.wall?.length > 0 && (
        <>
          <p className="text-[10px] font-mono uppercase tracking-widest text-stone-400 mt-4">
            Strongest relationships · by projects commissioned
          </p>
          <div className="flex flex-wrap gap-2 mt-2">
            {data.wall.map((c) => (
              <span
                key={c.name}
                className="inline-flex items-baseline gap-1.5 rounded-full border border-[#2a9d8f]/25 bg-[#2a9d8f]/5 px-3 py-1"
              >
                <span className="font-serif text-sm text-slate-900">{c.name}</span>
                <span className="text-[10px] font-mono text-[#1f7468]">
                  {c.years} yrs · {c.projects} projects
                </span>
              </span>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------- lost reasons */

// The BD team's lost-reason taxonomy - what each option means when
// feedback comes back from the client.
const REASON_DEFS = {
  "PRICE": "We were too expensive versus a competitor.",
  "FEASIBILITY": "A competitor had better sample feasibility.",
  "TIMELINES": "A competitor could deliver quicker.",
  "INCUMBENT": "The client chose a partner with a proven track record, or one already on their preferred supplier list.",
  "UNCONVINCING": "Our proposal was weak.",
  "CLIENT LOST": "Our client lost the wider bid - the end sponsor chose another company.",
  "CANCELLED": "The sponsor cancelled the opportunity, or our client never submitted a bid.",
  "OTHER": "Lost for a reason outside the standard list.",
  "UNKNOWN": "Our client's client did not provide an answer.",
  "NO RESPONSE": "The client did not tell us why after being chased 3 times over 3 months.",
  "ADD-ON": "Not a true loss - the opportunity became part of an existing project.",
};

function ReasonTip({ reason }) {
  const def = REASON_DEFS[(reason || "").toUpperCase()];
  if (!def) return null;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="shrink-0 text-stone-300 hover:text-[#1B4F8A] transition-colors"
          data-testid={`lost-reason-info-${(reason || "").toLowerCase().replace(/\s+/g, "-")}`}
          aria-label={`What ${reason} means`}
        >
          <Info className="w-3 h-3" />
        </button>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        className="max-w-xs bg-slate-900 text-white border border-slate-700 rounded-xl px-3.5 py-2.5 shadow-xl"
      >
        <p className="text-[9px] font-mono uppercase tracking-widest text-teal-300">{reason}</p>
        <p className="text-[11px] leading-snug mt-0.5 text-stone-100">{def}</p>
      </TooltipContent>
    </Tooltip>
  );
}

function ReasonBars({ reasons }) {
  const max = Math.max(...reasons.map((r) => r.n), 1);
  return (
    <div className="flex flex-col gap-1.5 mt-3">
      {reasons.map((r) => (
        <div key={r.reason} className="flex items-center gap-2">
          <span className="w-28 flex items-center gap-1 text-[10px] font-mono text-stone-500">
            <span className="truncate">{r.reason}</span>
            <ReasonTip reason={r.reason} />
          </span>
          <div className="flex-1 h-2.5 bg-stone-100 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full ${r.reason.toUpperCase() === "PRICE" ? "bg-[#2a9d8f]" : "bg-[#1B4F8A]/50"}`}
              style={{ width: `${Math.max((r.n / max) * 100, 1.5)}%` }}
            />
          </div>
          <span className="w-10 text-right text-[10px] font-mono text-stone-500">{r.n}</span>
          <span className="w-14 text-right text-[10px] font-mono text-stone-400">
            {r.value > 0 ? moneyShort(r.value) : ""}
          </span>
        </div>
      ))}
    </div>
  );
}

export function LostReasons({ data, footer }) {
  const q = data?.quarter;
  if (!q || !q.lost) return null;
  return (
    <div className="rounded-2xl border border-[#1B4F8A]/10 bg-[#eef4fa] shadow-sm shadow-[#1B4F8A]/5 p-5 q-card q-reveal" data-testid="qkpi-lost-reasons">
      <div className="flex items-center gap-2">
        <PoundSterling className="w-4 h-4 text-[#a07708]" />
        <p className="text-[10px] font-mono uppercase tracking-widest text-stone-400">
          When we don&apos;t win, why
        </p>
        <InfoTip id="lost_reasons" />
      </div>
      <div className="mt-2" data-testid="lost-reasons-quarter">
        <p className="font-serif text-lg text-slate-900 leading-snug">
          <strong>{q.lost}</strong> of {q.label || "this quarter"}&apos;s quotes lost so far.
        </p>
        <p className="text-[11px] text-stone-500 mt-1" data-testid="lost-reasons-source-note">
          The breakdown below is based on the reasons clients gave us for not going ahead with the
          proposal. Where a client did not tell us why after being chased 3 times over 3 months, the
          quote is counted as No Response.
        </p>
        <ReasonBars reasons={q.reasons || []} />
      </div>
      {footer}
    </div>
  );
}

/* ------------------------------------------------- work mix */

export function useInView(threshold = 0.25) {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || seen) return undefined;
    if (!("IntersectionObserver" in window)) {
      setSeen(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setSeen(true);
          io.disconnect();
        }
      },
      { threshold },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [seen, threshold]);
  return [ref, seen];
}

function MixDelta({ delta, cmp }) {
  if (delta == null) return <span className="text-[10px] font-mono text-stone-300">new</span>;
  if (delta === 0) {
    return (
      <span className="text-[10px] font-mono text-stone-400" title={`Same as ${cmp}.`}>
        <Minus className="w-2.5 h-2.5 inline -mt-0.5" /> same
      </span>
    );
  }
  const up = delta > 0;
  return (
    <span
      className={`text-[10px] font-mono font-medium ${up ? "text-[#1B4F8A]" : "text-stone-500"}`}
      title={`${up ? `${delta} more` : `${-delta} fewer`} than ${cmp}. A mix shift, not a score - neither direction is 'good' by itself.`}
    >
      {up ? (
        <ArrowUp className="w-2.5 h-2.5 inline -mt-0.5" />
      ) : (
        <ArrowDown className="w-2.5 h-2.5 inline -mt-0.5" />
      )}
      {up ? "+" : "−"}{Math.abs(delta)}
    </span>
  );
}

function MixGroup({ title, rows, palette, cmp, seen, offset }) {
  if (!rows?.length) return null;
  const max = Math.max(...rows.map((r) => Math.max(r.n, r.prev || 0)), 1);
  const total = rows.reduce((s, r) => s + r.n, 0);
  return (
    <div>
      <p className="text-[10px] font-mono uppercase tracking-widest text-stone-400">{title}</p>
      <div className="flex flex-col gap-2.5 mt-3">
        {rows.map((r, i) => {
          const delta = r.prev != null ? r.n - r.prev : null;
          const hex = palette[i % palette.length];
          const share = total ? Math.round((100 * r.n) / total) : 0;
          return (
            <div
              key={r.label}
              className="flex items-center gap-2.5"
              title={`${r.label}: ${r.n} projects this quarter (${share}% of the mix)${
                r.prev != null ? ` · ${cmp}: ${r.prev} (the dark tick on the bar)` : ""
              }`}
            >
              <span className="w-36 sm:w-44 shrink-0 truncate text-[10px] font-mono text-stone-600">
                {r.label}
              </span>
              <div className="relative flex-1 h-3 bg-stone-100 rounded-full">
                <div
                  className="absolute inset-y-0 left-0 rounded-full q-grow"
                  style={{
                    width: `${Math.max((r.n / max) * 100, 2)}%`,
                    backgroundColor: hex,
                    animationDelay: `${offset + i * 90}ms`,
                    animationPlayState: seen ? "running" : "paused",
                  }}
                />
                {r.prev != null && r.prev > 0 && (
                  <div
                    className="absolute -top-[3px] -bottom-[3px] w-[2px] rounded bg-slate-800/70 q-tickin"
                    style={{
                      left: `calc(${(r.prev / max) * 100}% - 1px)`,
                      animationDelay: `${offset + i * 90 + 550}ms`,
                      animationPlayState: seen ? "running" : "paused",
                    }}
                  />
                )}
              </div>
              <span className="w-9 shrink-0 text-right text-[11px] font-mono font-semibold text-slate-900">
                {r.n}
              </span>
              <span className="w-12 shrink-0 text-right">
                <MixDelta delta={delta} cmp={cmp} />
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function WorkMix({ data, cmp = "the same quarter last year", footer }) {
  const [ref, seen] = useInView();
  if (!data || (!data.qual_quant?.length && !data.study_type?.length)) return null;
  const movers = [...(data.qual_quant || []), ...(data.study_type || [])]
    .filter((r) => r.prev != null)
    .map((r) => ({ ...r, delta: r.n - r.prev }))
    .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));
  const top = movers[0] && movers[0].delta !== 0 ? movers[0] : null;
  return (
    <div
      ref={ref}
      className="rounded-2xl border border-[#1B4F8A]/10 bg-[#eef4fa] shadow-sm shadow-[#1B4F8A]/5 p-5 flex flex-col gap-5 q-card q-reveal"
      data-testid="qkpi-work-mix"
    >
      <div>
        <p className="text-[10px] font-mono uppercase tracking-widest text-stone-400 flex items-center gap-1.5">
          The work we delivered this quarter · vs {cmp}
          <InfoTip id="work_mix" />
        </p>
        {top && (
          <p className="font-serif text-lg text-slate-900 mt-1.5 leading-snug" data-testid="work-mix-mover">
            Biggest shift: <strong>{top.label}</strong>,{" "}
            {top.delta > 0 ? (
              <>up <strong className="text-[#1B4F8A]">{top.delta}</strong></>
            ) : (
              <>down <strong>{-top.delta}</strong></>
            )}{" "}
            on {cmp}.
          </p>
        )}
        <p className="text-[10px] text-stone-400 mt-0.5">
          Solid bar = this quarter · dark tick = where {cmp} sat.
        </p>
      </div>
      <MixGroup
        title="Qual vs Quant"
        rows={data.qual_quant}
        palette={["#1B4F8A", "#2a9d8f", "#d4a373", "#a8a29e"]}
        cmp={cmp}
        seen={seen}
        offset={0}
      />
      <MixGroup
        title="Study types"
        rows={data.study_type}
        palette={["#1B4F8A", "#2a9d8f", "#d4a373", "#f4a261", "#e76f51", "#a8a29e"]}
        cmp={cmp}
        seen={seen}
        offset={350}
      />
      {footer}
    </div>
  );
}

/* ------------------------------------------------- seasonality */

export function Seasonality({ data }) {
  if (!data?.length) return null;
  const max = Math.max(...data.map((m) => m.n), 1);
  const min = Math.min(...data.map((m) => m.n));
  return (
    <div className="rounded-2xl border border-[#1B4F8A]/10 bg-[#eef4fa] shadow-sm shadow-[#1B4F8A]/5 p-5 q-card q-reveal" data-testid="qkpi-seasonality">
      <p className="text-[10px] font-mono uppercase tracking-widest text-stone-400 flex items-center gap-1.5">
        When we&apos;re busiest · projects in field by month, since Jan 2023
        <InfoTip id="seasonality" />
      </p>
      <div className="grid grid-cols-6 sm:grid-cols-12 gap-1.5 mt-3">
        {data.map((m) => {
          const s = max > min ? (m.n - min) / (max - min) : 0.5;
          return (
            <div key={m.month} className="flex flex-col items-center gap-1">
              <div
                className="w-full h-10 rounded-lg flex items-end justify-center pb-0.5"
                style={{ backgroundColor: `rgba(27, 79, 138, ${0.08 + s * 0.82})` }}
                title={`${m.month}: ${m.n} projects in field`}
              >
                <span className={`text-[9px] font-mono ${s > 0.5 ? "text-white" : "text-slate-700"}`}>
                  {m.n}
                </span>
              </div>
              <span className="text-[9px] font-mono text-stone-400">{m.month}</span>
            </div>
          );
        })}
      </div>
      <p className="text-[10px] text-stone-400 mt-2">Darker = busier. Handy for planning leave.</p>
    </div>
  );
}

/* ------------------------------------------------- milestones */

export function Milestones({ data, records, footer }) {
  const recs = records || [];
  const fastest = data?.fastest_delivery;
  if (!recs.length && !fastest) return null;
  const fmtRec = (r, v) =>
    r.money ? moneyShort(v) : Math.round(v).toLocaleString("en-GB");
  return (
    <div className="q-reveal" data-testid="qkpi-milestones">
      <h3 className="text-[10px] font-mono uppercase tracking-[0.25em] text-stone-400 mb-3 flex items-center gap-2">
        <Trophy className="w-3.5 h-3.5 text-[#d6a419]" />
        Records &amp; milestones
        <InfoTip id="milestones" />
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {recs.map((r) => (
          <div
            key={r.id}
            className="rounded-2xl border border-[#d6a419]/40 bg-gradient-to-br from-[#fdf6e3] to-[#f6e8c9] shadow-sm p-4 flex flex-col gap-1 q-card"
            data-testid={`qkpi-record-${r.id}`}
          >
            <p className="text-[9px] font-mono uppercase tracking-widest text-[#8a6d1d] flex items-center gap-1.5">
              <Trophy className="w-3 h-3" /> All-time record
            </p>
            <p className="font-semibold tracking-tight text-2xl text-slate-900 leading-none mt-1">
              {fmtRec(r, r.value)}
            </p>
            <p className="text-[11px] text-stone-600 leading-snug">{r.label}</p>
            <p className="text-[10px] text-stone-400 leading-snug">
              Beats {r.prev_quarter}&apos;s {fmtRec(r, r.prev_value)}.
            </p>
          </div>
        ))}
        {fastest && (
          <div
            className="rounded-2xl border border-[#d4a373]/30 bg-[#faf5ec] shadow-sm p-4 flex flex-col gap-1 q-card"
            data-testid="qkpi-milestone-fastest"
          >
            <p className="text-[9px] font-mono uppercase tracking-widest text-[#9c5620]">
              Fastest delivery this quarter
            </p>
            <p className="font-semibold tracking-tight text-2xl text-slate-900 leading-none mt-1">
              {fastest.days} days
            </p>
            <p className="text-[11px] text-stone-600 leading-snug">
              In field to finished · {fastest.client}
            </p>
          </div>
        )}
      </div>
      {footer}
    </div>
  );
}
